import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import {
  FancyAnchorLink,
  IActionDropdownItem,
  Pager,
  Table,
  TableColumn,
  TableSkeleton,
} from "shared/components";
import {
  BAND_ACTIONS,
  EventTravelLegType,
  IBandUser,
  ITravelBlockBookingTicket,
  ITravelBlockPassenger,
} from "Bands/types";
import { Spacer } from "shared/layout";
import { getTravelLegTitle, useClearActionMetaOnUnMount, usePagination } from "Bands/utils";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { MutedDarkText, MutedText, SmallText, TinyText } from "shared/styles";
import {
  dataMetaSelector,
  dispatchGetTravelBlockPassengers,
  travelBlockPassengersSelectorCreator,
} from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import { MemberName } from "Bands/components";
import { DeleteTravelBlockPassengerModal, EditTravelBlockPassengerModal } from "Bands/containers";
import { omit } from "lodash-es";
import { NotFlexSpacer } from "Bands/containers/EventSchedule/styles";

const tableCopy = copy.travelBlock.passenger;

interface Props {
  blockId: string;
  travelLegs: EventTravelLegType[];
  showActions?: boolean;
  isPrint?: boolean;
}

export const PassengerTable: FC<Props> = ({ blockId, travelLegs, showActions, isPrint }) => {
  const [showEditModal, setShowEditModal] = useState<ITravelBlockPassenger | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<ITravelBlockPassenger | null>(null);

  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getTravelBlockPassengers));

  useClearActionMetaOnUnMount(BAND_ACTIONS.getTravelBlockPassengers);

  const pagination = usePagination(API_ENDPOINTS.EVENT_TRAVEL_BLOCK(blockId));

  const passengersSelector = useMemo(() => {
    const { pages, currentPage } = pagination;

    return travelBlockPassengersSelectorCreator(pages?.[currentPage]);
  }, [pagination]);
  const passengers = useSelector(passengersSelector);

  const getRowActions = useCallback(
    (id: string): IActionDropdownItem[] => {
      if (!showActions) return [];

      const passenger = passengers.find(passenger => passenger.id === id);

      if (!passenger) return [];

      return [
        {
          label: copy.actions.edit,
          dataTestId: "edit_passenger",
          handleClick: () => setShowEditModal(passenger),
        },
        {
          label: copy.actions.delete,
          handleClick: () => setShowDeleteModal(passenger),
          status: IStatus.error,
        },
      ];
    },
    [passengers, showActions]
  );

  const fetchPassengers = useCallback(
    (page?: number) => {
      dispatchGetTravelBlockPassengers({
        dispatch,
        page,
        blockId,
        isRecursive: isPrint,
      });
    },
    [blockId, dispatch, isPrint]
  );

  useEffect(() => {
    fetchPassengers();
  }, [fetchPassengers]);

  return (
    <NotFlexSpacer size="s">
      {showEditModal && (
        <EditTravelBlockPassengerModal
          blockId={blockId}
          travelLegs={travelLegs}
          handleClose={() => setShowEditModal(null)}
          passenger={showEditModal}
        />
      )}
      {showDeleteModal && (
        <DeleteTravelBlockPassengerModal
          blockId={blockId}
          handleClose={() => setShowDeleteModal(null)}
          passenger={showDeleteModal}
        />
      )}
      {apiMeta?.status === IAPIState.pending ? (
        <TableSkeleton
          nbRows={passengers.length ? passengers.length + 1 : 16}
          theme={ITheme.tertiary}
        />
      ) : (
        <Table
          data={passengers}
          columns={getColumns(travelLegs)}
          getRowActions={getRowActions}
          emptyCopy={tableCopy.messages.empty}
          options={{
            verticalAlignCells: "top",
            showActions,
          }}
          dataTestId="passengers_table"
        />
      )}

      <Pager
        {...pagination}
        onPageChange={fetchPassengers}
        isDisabled={apiMeta?.status === IAPIState.pending}
      />
    </NotFlexSpacer>
  );
};

const getColumns = (travelLegs: EventTravelLegType[]): TableColumn[] => [
  {
    Header: tableCopy.fields.name,
    accessor: "user",
    Cell: ({ value }) => {
      const user = value as IBandUser;

      return <MemberName user={user} />;
    },
  },
  {
    Header: `${tableCopy.fields.bookingReference}/${tableCopy.fields.ticket}`,
    accessor: "bookingTickets",
    Cell: ({ value }) => {
      const tickets = value as ITravelBlockBookingTicket[];

      return (
        <Spacer size="xs">
          {tickets.map(({ bookingReference, files, travelLeg: { id: travelLegPath } }) => {
            const travelLegIndex = travelLegs.findIndex(({ id }) => travelLegPath.includes(id));
            const travelLeg = travelLegs[travelLegIndex];
            const travelLegTitle = travelLeg
              ? getTravelLegTitle(omit(travelLeg, ["departure", "arrival"]))
              : null;

            return (
              <div key={travelLegIndex}>
                {travelLegTitle && (
                  <TinyText as={MutedDarkText}>
                    #{travelLegIndex + 1} {travelLegTitle.prefix}
                    {bookingReference && ":"}{" "}
                  </TinyText>
                )}
                {bookingReference && <SmallText as={MutedText}>{bookingReference}</SmallText>}
                {bookingReference && files[0] && <SmallText as={MutedDarkText}> • </SmallText>}
                {files[0]?.source && (
                  <SmallText>
                    <FancyAnchorLink href={files[0].source} target="_blank">
                      {tableCopy.fields.ticket}
                    </FancyAnchorLink>
                  </SmallText>
                )}
              </div>
            );
          })}
        </Spacer>
      );
    },
  },
];
